/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import TeamMemberList from '@/components/conversation-insights/team-member-list';
import { RootState } from '@/store/reducers';
import { API_BASE_URL } from '@/utils/constants';
import HttpClient from '@/utils/http-client';
import { Member } from '@/utils/types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

type Props = {
  workspaceId: number;
  organisationId: number;
  insightsFilters: any;
  openZipInsights: boolean;
  handleLeaderboardUserClick: Function;
  handleLeaderboardUserRemove: Function;
  authToken?: string;
};

const Leaderboard = ({
  workspaceId,
  organisationId,
  insightsFilters,
  openZipInsights,
  handleLeaderboardUserClick,
  handleLeaderboardUserRemove,
  authToken,
}: Props) => {
  const commonHeaders = {
    Authorization: `Bearer ${authToken}`,
    'api-client-key': process.env.NEXT_PUBLIC_PARTNER_API_CLIENT_KEY!,
    'api-client-secret': process.env.NEXT_PUBLIC_PARTNER_API_CLIENT_SECRET!,
    'Content-Type': 'application/json',
  };

  const [wpUserObj, setWpUserObj] = useState({});
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>('total_conversations');
  const [memberList, setMemberList] = useState<Member[]>([]);
  const [memberListLoading, setMemberListLoading] = useState<boolean>(false);
  const [memberListAPIError, setMemberListAPIError] = useState<boolean>(false);
  const [noDataFetch, setNoDataFetch] = useState<boolean>(false);
  const [filters, setFilters] = useState<{ [key: string]: string }[]>([]);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(false);
  const [filtersAPIError, setFiltersAPIError] = useState<boolean>(false);

  const fetchUserMembers = async (wpId: number) => {
    try {
      setMemberListLoading(true);
      const params = {
        page: page?.toString(),
        sort_field: sortField,
        sort_order: sort,
      };
      const queryString = new URLSearchParams(params)?.toString?.();

      if (authToken) {
        const url = `${API_BASE_URL}/v1/client/customer/insights/team-data/workspace/${wpId}${
          queryString ? `?${queryString}` : ''
        }`;
        const res = await fetch(url, {
          method: 'GET',
          headers: commonHeaders,
        });
        if (!res.ok) throw new Error(res.statusText);

        const body = await res.json();
        const data = (body.data as any) as Member[];

        if (page === 1) {
          setMemberList(data || []);
          setNoDataFetch(false);
        } else {
          if (!data?.length) setNoDataFetch(true);
          else setNoDataFetch(false);
          setMemberList(prev => [...prev, ...(data || [])]);
        }
      } else {
        const response = await HttpClient.get(
          `${API_BASE_URL}/v1/user-performance/team-data/workspace/${wpId}${
            queryString ? `?${queryString}` : ''
          }`,
        );
        const { data } = response.data;
        if (page === 1) {
          setMemberList(data || []);
          setNoDataFetch(false);
        } else {
          if (!data?.length) setNoDataFetch(true);
          else setNoDataFetch(false);
          setMemberList(prev => [...prev, ...(data || [])]);
        }
      }
    } catch (error) {
      setMemberListAPIError(true);
      console.log(error);
    } finally {
      setMemberListLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      setFiltersLoading(true);
      if (authToken) {
        const res = await fetch(`${API_BASE_URL}/v1/client/customer/insights/performance/filters`, {
          method: 'GET',
          headers: commonHeaders,
        });
        if (!res.ok) throw new Error(res.statusText);

        const body = await res.json();
        // body.data is the array of filters
        const data = body.data as Array<{ [key: string]: string }>;
        if (!data) {
          setFiltersAPIError(true);
        } else {
          setFilters(data);
        }
      } else {
        const response = await HttpClient.get(
          `${API_BASE_URL}/v1/user-performance/filters`,
        );
        if (!response?.data?.data) setFiltersAPIError(true);
        const { data } = response.data;
        setFilters(data);
      }
    } catch (error) {
      setFiltersAPIError(true);
      console.log(error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchWpUsers = async (orgId: number, currentWorkspaceId: number) => {
    try {
      if (authToken) {
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/workspace/${currentWorkspaceId}/users`,
          {
            method: 'GET',
            headers: commonHeaders,
          },
        );
        if (!res.ok) throw new Error(res.statusText);

        const body = await res.json();
        const users = body.data as Array<any>;
        const filteredUsers = users
          .filter(
            (u: any) =>
              u.status === 'active' &&
              !u.email.startsWith('unassigned') &&
              !u.email.startsWith('zipteams'),
          )
          .map((u: any) => {
            const name = `${u.first_name}${
              u.last_name ? ` ${u.last_name}` : ''
            }`.trim();
            return {
              label: name,
              value: u.email,
              avatar: u.avatar_url,
              id: u.user_id,
              name,
            };
          });

        const sortedUsers = filteredUsers.sort((a, b) =>
          a.label === 'Me'
            ? -1
            : b.label === 'Me'
            ? 1
            : a.label.localeCompare(b.label),
        );

        setWorkspaceUsers(sortedUsers);
      } else {
        const response = await HttpClient.get(
          `${API_BASE_URL}/v1/organisation/${orgId}/workspace/${currentWorkspaceId}/users`,
        );
        const data = await response.data;
        const users = data?.data;
        const filteredUsers = (users || [])
          .filter(
            (eachUser: any) =>
              eachUser?.status === 'active' &&
              !(
                eachUser?.email?.startsWith('unassigned') ||
                eachUser?.email?.startsWith('zipteams')
              ),
          )
          .map((eachUser: any) => {
            const name = `${eachUser.first_name}${
              eachUser.last_name ? ` ${eachUser.last_name}` : ''
            }`.trim();
            return {
              label: name,
              // label: eachUser?.email === user?.email ? 'Me' : name,
              value: eachUser.email,
              avatar: eachUser?.avatar_url,
              id: eachUser?.user_id,
              name,
            };
          });

        const sortedUsers = filteredUsers.sort((a, b) => {
          if (a.label === 'Me') return -1;
          if (b.label === 'Me') return 1;
          return a.label.localeCompare(b.label);
        });

        setWorkspaceUsers(sortedUsers);
      }
    } catch (error) {
      console.error('fetchWpUsers error', error);
    }
  };

  useEffect(() => {
    if (!memberListLoading && workspaceId) {
      fetchUserMembers(workspaceId);
    }
  }, [page, sortField, sort, workspaceId]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (organisationId && workspaceId) {
      fetchWpUsers(organisationId, workspaceId);
    }
  }, [organisationId, workspaceId]);

  useEffect(() => {
    if (workspaceUsers?.length) {
      const result = workspaceUsers.reduce((acc, item) => {
        if (item?.id) {
          acc[item?.id] = item;
        }
        return acc;
      }, {});
      setWpUserObj(result);
    }
  }, [workspaceUsers]);
  return (
    <TeamMemberList
      noDataFetch={noDataFetch}
      filterProps={{
        page,
        setPage,
        sortField,
        setSortField,
        sort,
        setSort,
      }}
      filters={filters}
      memberListLoading={memberListLoading}
      memberListAPIError={memberListAPIError}
      memberList={memberList}
      workspaceUsersObj={wpUserObj}
      insightsFilters={insightsFilters}
      openZipInsights={openZipInsights}
      handleLeaderboardUserClick={handleLeaderboardUserClick}
      handleLeaderboardUserRemove={handleLeaderboardUserRemove}
    />
  );
};

export default Leaderboard;
